import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';

// Route Supabase sends users to from the confirmation email. Exported so the
// route definition and the signup/confirm pages cannot drift apart.
export const EMAIL_CONFIRM_PATH = '/auth/confirm';

// Minimum password length, shared by every screen that sets one. Defined here
// so signup and reset cannot drift: if reset allowed fewer characters than
// signup, a user could reset their way to a weaker password than we permit.
export const MIN_PASSWORD_LENGTH = 8;

interface User {
  id: string;
  email: string;
  name: string | null;
  isAdmin: boolean;
  isApproved: boolean;
  pendingApproval: boolean;
  isSubscribed: boolean;
  subscriptionExpiresAt?: string | null;
  subscriptionPlan?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  pendingApproval: boolean;
  isSubscribed: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; pendingApproval?: boolean }>;
  register: (email: string, password: string, name?: string, adminCode?: string) => Promise<{ success: boolean; error?: string; pendingApproval?: boolean }>;
  resendConfirmation: (email: string) => Promise<{ success: boolean; error?: string }>;
  devLogin: (secretCode: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  checkAuth: () => Promise<void>;
  syncAuthSession: (session: { accessToken: string; refreshToken?: string; user: User }) => void;
}

function mapSupabaseUser(supabaseUser: SupabaseUser): User {
  const meta = supabaseUser.user_metadata ?? {};
  const appMeta = supabaseUser.app_metadata ?? {};
  return {
    id: supabaseUser.id,
    email: supabaseUser.email ?? '',
    name: meta.name ?? meta.full_name ?? null,
    isAdmin: appMeta.is_admin ?? false,
    isApproved: appMeta.is_approved ?? true,
    pendingApproval: appMeta.pending_approval ?? false,
    isSubscribed: appMeta.is_subscribed ?? false,
    subscriptionExpiresAt: appMeta.subscription_expires_at ?? null,
    subscriptionPlan: appMeta.subscription_plan ?? null,
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.isAdmin ?? false;
  const isApproved = user?.isApproved ?? false;
  const pendingApproval = user?.pendingApproval ?? false;
  const isSubscribed = user?.isSubscribed ?? false;

  // Listen to Supabase auth state — handles session restore, token refresh, sign out
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(mapSupabaseUser(session.user));
        setToken(session.access_token);
      } else {
        setUser(null);
        setToken(null);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAuth = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setUser(mapSupabaseUser(session.user));
      setToken(session.access_token);
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const register = async (email: string, password: string, name?: string, adminCode?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Where Supabase sends the user after they click the confirmation link.
        emailRedirectTo: `${window.location.origin}${EMAIL_CONFIRM_PATH}`,
        data: {
          ...(name ? { name } : {}),
          ...(adminCode ? { admin_code: adminCode } : {}),
        },
      },
    });

    if (error) return { success: false, error: error.message };

    // If email confirmation is required, session will be null
    if (data.user && !data.session) {
      return { success: true, pendingApproval: true };
    }

    return { success: true };
  };

  const resendConfirmation = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}${EMAIL_CONFIRM_PATH}` },
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const refreshToken = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) return false;
    setToken(data.session.access_token);
    return true;
  };

  // devLogin is kept for interface compatibility — wire to a real backend route if needed
  const devLogin = async (_secretCode: string) => {
    return { success: false, error: 'Dev login is not supported with Supabase auth.' };
  };

  const syncAuthSession = useCallback((session: { accessToken: string; refreshToken?: string; user: User }) => {
    setToken(session.accessToken);
    setUser(session.user);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        isAdmin,
        isApproved,
        pendingApproval,
        isSubscribed,
        login,
        register,
        resendConfirmation,
        devLogin,
        logout,
        refreshToken,
        checkAuth,
        syncAuthSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
